
import { motion } from 'framer-motion';
import { useState } from 'react';
import Button from '../../../components/base/Button';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // バリデーション
    if (!formData.name || !formData.email || !formData.message) {
      return;
    }

    setShowConfirmation(true);
  };

  const handleConfirmedSubmit = async () => {
    setIsSubmitting(true);

    try {
      // フォームデータを準備
      const submitData = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        submitData.append(key, value);
      });

      // フォーム送信
      await fetch('https://readdy.ai/api/form/d3vgk5bjbvf464v00td0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData
      });

      // 成功時の処理
      setShowConfirmation(false);
      setShowThankYou(true);
      setFormData({
        company: '',
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
    } catch (error) {
      console.error('送信エラー:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getServiceDisplayName = (value: string) => {
    const serviceMap: Record<string, string> = {
      'event': 'イベント関連',
      'display': 'ディスプレイ・商業看板',
      'digital': 'デジタルサイネージ',
      'marketing': 'マーケティング',
      'consulting': 'コンサルティング',
      'ai': 'AI関連',
      'other': 'その他'
    };
    return serviceMap[value] || value;
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">お問い合わせ</h2>
          <p className="text-xl text-gray-600">お気軽にご相談ください</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">お問い合わせ先</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-blue-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">所在地</h4>
                    <p className="text-gray-600 text-sm">
                      〒371-0854<br />
                      群馬県前橋市大渡町1-23-13
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">電話番号</h4>
                    <p className="text-gray-600 text-sm">
                      TEL: 027-255-2214<br />
                      FAX: 027-255-2324
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-purple-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">メールアドレス</h4>
                    <p className="text-gray-600 text-sm">infovan@shinnitto.jp</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">営業時間</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">平日</span>
                  <span className="font-semibold">9:00 - 18:00</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">土日祝</span>
                  <span className="text-red-500">休業</span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl p-8 shadow-lg"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6">お問い合わせフォーム</h3>
            
            <form id="contact-form" data-readdy-form onSubmit={handleFormSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                    会社名
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="株式会社○○"
                  />
                </div>
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="山田太郎"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm-grid-cols-2 gap-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="example@company.com"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="027-000-0000"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                  ご興味のあるサービス
                </label>
                <div className="relative">
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8 appearance-none"
                  >
                    <option value="">選択してください</option>
                    <option value="event">イベント関連</option>
                    <option value="display">ディスプレイ・商業看板</option>
                    <option value="digital">デジタルサイネージ</option>
                    <option value="marketing">マーケティング</option>
                    <option value="consulting">コンサルティング</option>
                    <option value="ai">AI関連</option>
                    <option value="other">その他</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl pointer-events-none"></i>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  お問い合わせ内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                  placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                />
                <div className="text-right text-xs text-gray-500 mt-1">
                  {formData.message.length}/500文字
                </div>
              </div>

              <Button 
                type="submit" 
                size="lg" 
                className="w-full whitespace-nowrap"
                disabled={formData.message.length > 500}
              >
                入力内容を確認
              </Button>
            </form>
          </motion.div>
        </div>

        {/* 確認画面モーダル */}
        {showConfirmation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            >
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-bold text-gray-900">入力内容の確認</h3>
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="space-y-6">
                {formData.company && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">会社名</h4>
                    <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{formData.company}</p>
                  </div>
                )}
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">お名前</h4>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{formData.name}</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">メールアドレス</h4>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{formData.email}</p>
                </div>

                {formData.phone && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">電話番号</h4>
                    <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{formData.phone}</p>
                  </div>
                )}

                {formData.service && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">ご興味のあるサービス</h4>
                    <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{getServiceDisplayName(formData.service)}</p>
                  </div>
                )}

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">お問い合わせ内容</h4>
                  <div className="text-gray-700 bg-gray-50 p-3 rounded-lg whitespace-pre-wrap">{formData.message}</div>
                </div>
              </div>

              <div className="mt-8 flex gap-4 justify-center">
                <Button 
                  variant="outline" 
                  onClick={() => setShowConfirmation(false)}
                  className="whitespace-nowrap"
                >
                  修正する
                </Button>
                <Button 
                  onClick={handleConfirmedSubmit}
                  disabled={isSubmitting}
                  className="whitespace-nowrap"
                >
                  {isSubmitting ? '送信中...' : '送信する'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* お礼メッセージモーダル */}
        {showThankYou && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-2xl p-8 max-w-md w-full text-center"
            >
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-check-line text-green-600 text-3xl"></i>
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4">送信完了</h3>
              
              <p className="text-gray-700 mb-6 leading-relaxed">
                お問い合わせありがとうございます。<br />
                担当者より順次ご連絡いたします。
              </p>
              
              <Button 
                onClick={() => setShowThankYou(false)}
                className="whitespace-nowrap"
              >
                閉じる
              </Button>
            </motion.div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
